import { useState, useCallback } from "react";

/**
 * Custom hook to manage confirmation popup state
 * @returns {Object} - Object containing popup state and control functions
 */
export const useConfirmationPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [config, setConfig] = useState({
    title: "CONFIRMATION",
    message: "Are you sure you want to proceed?",
    confirmText: "CONFIRM",
    cancelText: "CANCEL",
    confirmButtonType: "primary",
    onConfirm: null,
    onCancel: null,
  });

  /**
   * Show confirmation popup with custom configuration
   * @param {Object} popupConfig - Configuration object for the popup
   */
  const showConfirmation = (popupConfig) => {
    
    if (isVisible) {
      return;
    }

    setConfig({
      ...config,
      ...popupConfig,
    });
    setIsVisible(true);
  };

  /**
   * Hide confirmation popup
   */
  const hideConfirmation = useCallback(() => {
    setIsVisible(false);
  }, []);

  /**
   * Handle confirm action
   */
  const handleConfirm = useCallback(() => {
    if (config.onConfirm) {
      config.onConfirm();
    }
    setIsVisible(false);
  }, [config]);

  /**
   * Handle cancel action
   */
  const handleCancel = useCallback(() => {
    if (config.onCancel) {
      config.onCancel();
    }
    setIsVisible(false);
  }, [config]);

  // Predefined confirmation types
  const showDeleteConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to delete ${itemName}?`,
      confirmText: "DELETE",
      cancelText: "CANCEL",
      confirmButtonType: "danger",
      onConfirm,
      onCancel,
    });
  };

  const showApproveConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to approve ${itemName}?`,
      confirmText: "APPROVE",
      cancelText: "CANCEL",
      confirmButtonType: "success",
      onConfirm,
      onCancel,
    });
  };

  const showRejectConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to reject ${itemName}?`,
      confirmText: "REJECT",
      cancelText: "CANCEL",
      confirmButtonType: "danger",
      onConfirm,
      onCancel,
    });
  };

  const showUpdateConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to update ${itemName}?`,
      confirmText: "UPDATE",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  const showSaveConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to save ${itemName}?`,
      confirmText: "SAVE",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  const showSubmitConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to submit ${itemName}?`,
      confirmText: "SUBMIT",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  const showDeactivateConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to deactivate ${itemName}?`,
      confirmText: "DEACTIVATE",
      cancelText: "CANCEL",
      confirmButtonType: "warning",
      onConfirm,
      onCancel,
    });
  };

  const showActivateConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to activate ${itemName}?`,
      confirmText: "ACTIVATE",
      cancelText: "CANCEL",
      confirmButtonType: "success",
      onConfirm,
      onCancel,
    });
  };

  const showPaidConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to mark ${itemName} as paid?`,
      confirmText: "MARK AS PAID",
      cancelText: "CANCEL",
      confirmButtonType: "success",
      onConfirm,
      onCancel,
    });
  };

  const showInvalidConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to mark ${itemName} as invalid?`,
      confirmText: "MARK AS INVALID",
      cancelText: "CANCEL",
      confirmButtonType: "warning",
      onConfirm,
      onCancel,
    });
  };

  const showSendReminderConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to send reminder to ${itemName}?`,
      confirmText: "SEND REMINDER",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  const showSendTemplateConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to send template to ${itemName}?`,
      confirmText: "SEND TEMPLATE",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  const showDownloadConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to download template for ${itemName}?`,
      confirmText: "DOWNLOAD",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  const showDownloadSignedConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to download signed PDF for ${itemName}?`,
      confirmText: "DOWNLOAD",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  const showVerifyConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to verify signed letter for ${itemName}?`,
      confirmText: "VERIFY",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  const showBulkReminderConfirmation = (itemName, onConfirm, onCancel) => {
    showConfirmation({
      title: "CONFIRMATION",
      message: `Are you sure you want to send bulk reminder to ${itemName}?`,
      confirmText: "SEND BULK REMINDER",
      cancelText: "CANCEL",
      confirmButtonType: "primary",
      onConfirm,
      onCancel,
    });
  };

  return {
    isVisible,
    config,
    showConfirmation,
    hideConfirmation,
    handleConfirm,
    handleCancel,
    // Predefined confirmation types
    showDeleteConfirmation,
    showApproveConfirmation,
    showRejectConfirmation,
    showUpdateConfirmation,
    showSaveConfirmation,
    showSubmitConfirmation,
    showDeactivateConfirmation,
    showActivateConfirmation,
    showPaidConfirmation,
    showInvalidConfirmation,
    // Partner compliance specific confirmations
    showSendReminderConfirmation,
    showSendTemplateConfirmation,
    showDownloadConfirmation,
    showDownloadSignedConfirmation,
    showVerifyConfirmation,
    showBulkReminderConfirmation,
  };
};

export default useConfirmationPopup;
